/**
 * Cart Drawer
 *
 * App-shell-level (not page-scoped) left-hand pull-out drawer for an
 * agent's carts - named, type-agnostic buckets of properties/
 * neighborhoods, collected before deciding what they're for. Unlike the
 * old AgentDrawer (which required picking a Buyer/Seller/Featured
 * destination before adding a single item), a cart has no type, no
 * status, no message, no recipients - it's just a list. For an ordinary
 * cart, items only ever leave by being COPIED into an actual message (see
 * the Messaging tab's "Add from Cart") - editing a cart afterward never
 * changes a message already built from it.
 *
 * One cart per agent is special: the undeletable, unrenameable default
 * "Featured Listings" cart (migration 067, per Todd 2026-10-25) every
 * agent has from the moment they register. Unlike ordinary carts, its
 * items are read LIVE, not copied - whatever's in it is what shows on the
 * agent's landing page and the map's `?agent=` Featured badge (see
 * workers/agent-api's renderFeaturedListingsBlock and workers/data-api's
 * agentFeatured.ts). Pinned first in the list below.
 *
 * Redesigned 2026-10-25 (per Todd) from a single dropdown + one expanded
 * cart to: every cart listed at once (scrollable), each independently
 * expandable to show its items inline, and a resizable panel (same
 * drag-handle mechanics as packages/ui-components' UnifiedDrawer, mirrored
 * for this panel's opposite anchor side). "Selected" (which cart the Agent
 * Pick checkbox adds to) and "expanded" (which carts show their items) are
 * two independent pieces of state - clicking a cart's row selects AND
 * expands it; clicking just its chevron only expands, so an agent can peek
 * into another cart without changing where new picks land.
 *
 * Same handle-to-open interaction as the map's other fixed-position
 * drawers (packages/ui-components' UnifiedDrawer/FilterDrawer/
 * DisplayDrawer), but instantiated once in main.ts rather than per-page -
 * see state/cartDrawer.ts for how pages reach the one shell-level
 * instance. setAvailable() controls whether the handle is shown at all
 * (Map/Messaging/Contacts only - see main.ts's route-change wiring),
 * distinct from open()/close() which toggle the panel within that.
 *
 * Clicking the map always opens the usual property/neighborhood info
 * modal, same as the public site; adding an item to the selected cart
 * happens from the "Agent Pick" checkbox inside that modal (see
 * hasItem()/toggleItem() below), not from the map click itself.
 *
 * Handle is vertically centered on the left edge, same as the map's other
 * drawer handles (just the opposite side from UnifiedDrawer's right-edge
 * one) - and like those, it's fixed-positioned independently of the panel,
 * so open()/close() explicitly slide it (via inline style, not CSS alone)
 * to the panel's edge when open rather than leaving it to overlay the
 * open panel at left:0.
 */
import type { AgentClient, AgentCartSummary, AgentCartDetail } from '@slowmaui/api-client';
import { getAttributeEntry } from '@slowmaui/ui-components';

// Same icon/color as the Featured attribute badge everywhere else it
// shows up (map pins, Listings drawer, CNTL filter) - per Todd 2026-10-26,
// so the default cart reads as "this is that same Featured" at a glance
// instead of an unrelated star.
const FEATURED_ATTRIBUTE = getAttributeEntry('featured');

export interface CartDrawerOptions {
  container: HTMLElement;
  client: AgentClient;
  /** Unlike Map (a full-bleed canvas, where floating drawers over it is
   *  the norm), Messaging/Contacts are ordinary scrolling page content -
   *  main.ts uses these to shrink #main-content so the open panel sits
   *  beside it instead of covering it, on those two pages only. */
  onOpen?: () => void;
  onClose?: () => void;
  /** Fires on every width change (drag-resize, or the stored width being
   *  restored on load) - main.ts uses this to keep #main-content's shrink
   *  margin in sync, same reason UnifiedDrawer exposes the same callback. */
  onResize?: (width: number) => void;
  /** Batched listing lookup (address label) for however many listing item
   *  ids a cart currently has - one call per cart expansion, not one per
   *  item. */
  resolveListingLabels: (ids: string[]) => Promise<Map<string, { label: string; status: string }>>;
  /** Synchronous neighborhood name lookup. */
  resolveNeighborhoodLabel: (id: string) => string | undefined;
  /** Batched TMK lookup (address label) for a parcel with no MLS listing
   *  history at all - item_type='tmk', migration 069. */
  resolveTmkLabels: (tmks: string[]) => Promise<Map<string, { label: string }>>;
}

const DEFAULT_WIDTH = 340;
const MIN_WIDTH = 280;
const MAX_WIDTH_PERCENT = 0.9;
const WIDTH_STORAGE_KEY = 'cartDrawerWidth';

export class CartDrawer {
  private container: HTMLElement;
  private client: AgentClient;
  private onOpen?: () => void;
  private onClose?: () => void;
  private onResize?: (width: number) => void;
  private resolveListingLabels: (ids: string[]) => Promise<Map<string, { label: string; status: string }>>;
  private resolveNeighborhoodLabel: (id: string) => string | undefined;
  private resolveTmkLabels: (tmks: string[]) => Promise<Map<string, { label: string }>>;

  private wrapper!: HTMLElement;
  private handle!: HTMLElement;
  private panel!: HTMLElement;
  private body!: HTMLElement;
  private resizeEl!: HTMLElement;
  private opened = false;
  private available = false;
  private width: number;

  private carts: AgentCartSummary[] = [];
  private cartsLoaded = false;
  /** Which cart "Agent Pick" adds to - distinct from which cart(s) are
   *  expanded below. */
  private selectedCartId: string | null = null;
  /** Which carts currently show their items. A cart can be expanded
   *  without being selected (just looking), or selected without being
   *  expanded (rare - selecting always expands too, but collapsing
   *  afterward doesn't deselect). */
  private expandedCartIds: Set<string> = new Set();
  /** Lazily fetched full detail (with items) per cart, keyed by cart id -
   *  fetched on first expand, refreshed after any mutation to that cart. */
  private cartDetails: Map<string, AgentCartDetail> = new Map();
  private loadingCartIds: Set<string> = new Set();
  private itemLabels: Map<string, string> = new Map();
  /** Which cart's name is currently showing as an inline <input> instead
   *  of static text (per Todd 2026-11-01 - no more prompt() popup for
   *  rename, and a brand-new cart starts straight in this state so the
   *  agent can just type the real name immediately). At most one at a
   *  time. */
  private editingCartId: string | null = null;

  constructor(options: CartDrawerOptions) {
    injectCartDrawerStyles();
    this.container = options.container;
    this.client = options.client;
    this.onOpen = options.onOpen;
    this.onClose = options.onClose;
    this.onResize = options.onResize;
    this.resolveListingLabels = options.resolveListingLabels;
    this.resolveNeighborhoodLabel = options.resolveNeighborhoodLabel;
    this.resolveTmkLabels = options.resolveTmkLabels;

    this.width = this.clampWidth(this.readStoredWidth() ?? DEFAULT_WIDTH);

    this.render();
    this.wireHandle();
    this.wireResize();
    this.renderBody();
  }

  // -------- Public API --------

  /** Shows/hides the handle entirely - called on route-change from
   *  main.ts. Map/Messaging/Contacts only; everywhere else this drawer
   *  simply doesn't exist on screen. Force-closes the panel when hidden
   *  rather than leaving it open-but-invisible. */
  setAvailable(available: boolean): void {
    this.available = available;
    if (!available) this.close();
    this.wrapper.classList.toggle('cart-drawer-unavailable', !available);
  }

  open(): void {
    if (this.opened || !this.available) return;
    this.opened = true;
    this.wrapper.classList.add('open');
    this.panel.style.width = `${this.width}px`;
    // Same convention as the map's other drawers (UnifiedDrawer etc.) -
    // the handle is fixed-positioned independently of the panel, so it has
    // to be explicitly slid to the panel's edge on open, or it just sits
    // at left:0 overlaying the now-open panel.
    this.handle.style.left = `${this.width}px`;
    this.handle.setAttribute('aria-expanded', 'true');
    this.onOpen?.();
  }

  close(): void {
    if (!this.opened) return;
    this.opened = false;
    this.wrapper.classList.remove('open');
    this.panel.style.width = '0';
    this.handle.style.left = '0';
    this.handle.setAttribute('aria-expanded', 'false');
    this.onClose?.();
  }

  toggle(): void {
    this.opened ? this.close() : this.open();
  }

  isOpen(): boolean {
    return this.opened;
  }

  getWidth(): number {
    return this.width;
  }

  /** Called by the PropertyModal/NeighborhoodModal Agent Pick checkbox
   *  once an agent has decided to add an item. Adds to whichever cart is
   *  currently selected - every agent always has at least the default
   *  Featured Listings cart, so there's always something to fall back to
   *  on first use. */
  async addItem(itemType: 'listing' | 'neighborhood' | 'tmk', itemId: string, label: string): Promise<void> {
    this.open();
    try {
      if (!this.cartsLoaded) await this.loadCarts();
      if (!this.selectedCartId) return; // no carts at all - shouldn't happen, every agent has the default
      await this.client.addCartItem(this.selectedCartId, { itemType, itemId });
      await this.refreshCart(this.selectedCartId);
      await this.loadCarts();
      this.showToast(`Added ${label}`);
    } catch (error) {
      console.error('[CartDrawer] Failed to add item:', error);
      this.showToast('Failed to add - try again');
    }
  }

  /** Whether the given listing/neighborhood is already in the selected
   *  cart - lets the modals' Agent Pick checkbox open pre-checked. */
  hasItem(itemType: 'listing' | 'neighborhood' | 'tmk', itemId: string): boolean {
    const cart = this.selectedCartId ? this.cartDetails.get(this.selectedCartId) : null;
    return !!cart?.items.some((i) => i.item_type === itemType && i.item_id === itemId);
  }

  /** Adds or removes an item depending on its current state - what the
   *  Agent Pick checkbox actually calls on click. */
  async toggleItem(itemType: 'listing' | 'neighborhood' | 'tmk', itemId: string, label: string): Promise<void> {
    const cart = this.selectedCartId ? this.cartDetails.get(this.selectedCartId) : null;
    const existing = cart?.items.find((i) => i.item_type === itemType && i.item_id === itemId);
    if (existing && this.selectedCartId) {
      await this.removeItem(this.selectedCartId, existing.id);
      this.showToast(`Removed ${label}`);
    } else {
      await this.addItem(itemType, itemId, label);
    }
  }

  showToast(message: string): void {
    const toast = document.createElement('div');
    toast.className = 'cart-drawer-toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('show'));
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  }

  destroy(): void {
    this.container.innerHTML = '';
  }

  // -------- Shell rendering --------

  private render(): void {
    this.container.innerHTML = `
      <div class="cart-drawer-wrapper cart-drawer-unavailable" id="cart-drawer-wrapper">
        <div class="cart-drawer-handle" id="cart-drawer-handle" title="Open / close your carts" aria-expanded="false">
          <span class="cart-drawer-handle-icon">🛒</span>
          <svg class="cart-drawer-handle-arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          <div class="cart-drawer-handle-label">Carts</div>
        </div>
        <div class="cart-drawer-panel" id="cart-drawer-panel">
          <div class="cart-drawer-body" id="cart-drawer-body"></div>
          <div class="cart-drawer-resize" id="cart-drawer-resize" title="Drag to resize">
            <div class="cart-drawer-resize-grip"></div>
          </div>
        </div>
      </div>
    `;

    this.wrapper = this.container.querySelector('#cart-drawer-wrapper') as HTMLElement;
    this.handle = this.container.querySelector('#cart-drawer-handle') as HTMLElement;
    this.panel = this.container.querySelector('#cart-drawer-panel') as HTMLElement;
    this.body = this.container.querySelector('#cart-drawer-body') as HTMLElement;
    this.resizeEl = this.container.querySelector('#cart-drawer-resize') as HTMLElement;
  }

  private wireHandle(): void {
    this.handle.addEventListener('click', () => this.toggle());
  }

  // Mirrors UnifiedDrawer's wireResize almost exactly, with the drag math
  // flipped: this panel is anchored at left:0 (grows rightward), vs.
  // UnifiedDrawer's right:0 (grows leftward), so dragging right INCREASES
  // width here instead of decreasing it.
  private wireResize(): void {
    let dragging = false;
    let startX = 0;
    let startWidth = 0;

    const onMouseDown = (e: MouseEvent) => {
      if (window.innerWidth < 768) return; // touch hit area too small to be useful
      dragging = true;
      startX = e.clientX;
      startWidth = this.width;
      this.resizeEl.classList.add('dragging');
      this.wrapper.classList.add('resizing');
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'ew-resize';
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      const delta = e.clientX - startX;
      this.setWidth(startWidth + delta);
    };

    const onMouseUp = () => {
      if (!dragging) return;
      dragging = false;
      this.resizeEl.classList.remove('dragging');
      this.wrapper.classList.remove('resizing');
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    this.resizeEl.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  private setWidth(width: number): void {
    this.width = this.clampWidth(width);
    if (this.opened) {
      this.panel.style.width = `${this.width}px`;
      this.handle.style.left = `${this.width}px`;
    }
    this.writeStoredWidth(this.width);
    this.onResize?.(this.width);
  }

  private clampWidth(w: number): number {
    const max = window.innerWidth * MAX_WIDTH_PERCENT;
    return Math.max(MIN_WIDTH, Math.min(max, w));
  }

  private readStoredWidth(): number | null {
    try {
      const raw = localStorage.getItem(WIDTH_STORAGE_KEY);
      if (!raw) return null;
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? n : null;
    } catch {
      return null;
    }
  }

  private writeStoredWidth(width: number): void {
    try {
      localStorage.setItem(WIDTH_STORAGE_KEY, String(Math.round(width)));
    } catch {
      // Ignore - resize still works for this session, just doesn't persist.
    }
  }

  // -------- Body content --------

  private renderBody(): void {
    if (!this.cartsLoaded) {
      this.body.innerHTML = `<p class="cart-drawer-hint">Loading...</p>`;
      void this.loadCarts().then(() => this.renderBody());
      return;
    }

    this.body.innerHTML = this.buildBodyContent();
    this.attachBodyListeners();
  }

  // Featured Listings pinned first regardless of recency, so it's always
  // easy to find.
  private displayCarts(): AgentCartSummary[] {
    return [...this.carts].sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));
  }

  private buildBodyContent(): string {
    return `
      <div class="cart-drawer-list-header">
        <span>Your Carts</span>
        <button type="button" class="cart-drawer-new-btn" title="Start a new cart">+ New</button>
      </div>
      <div class="cart-drawer-list">
        ${this.displayCarts().map((c) => this.buildCartRow(c)).join('')}
      </div>
    `;
  }

  private buildCartRow(cart: AgentCartSummary): string {
    const isSelected = this.selectedCartId === cart.id;
    const isExpanded = this.expandedCartIds.has(cart.id);
    const isLoading = this.loadingCartIds.has(cart.id);
    const isEditing = this.editingCartId === cart.id;
    const detail = this.cartDetails.get(cart.id);

    const chevron = `<svg class="cart-drawer-chevron${isExpanded ? ' expanded' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>`;
    const featuredBadge = cart.is_default
      ? `<svg class="cart-drawer-featured-badge" viewBox="0 0 24 24" fill="${FEATURED_ATTRIBUTE?.color ?? '#14b8a6'}">${FEATURED_ATTRIBUTE?.icon ?? ''}</svg>`
      : '';

    // Inline <input> instead of a prompt() popup (per Todd 2026-11-01) -
    // a brand-new cart (createNewCart sets editingCartId to the new id
    // before the caller's renderBody runs) starts straight in this
    // state, so the agent can just type the real name immediately
    // instead of seeing "Untitled cart" and needing a separate rename
    // click. Rename/delete buttons hide while editing - the input itself
    // is the rename UI now (commit/cancel via Enter/Escape/blur, wired
    // in attachBodyListeners).
    const nameHtml = isEditing
      ? `<input type="text" class="cart-drawer-cart-name-input" value="${escapeAttr(cart.name)}" maxlength="80">`
      : `<span class="cart-drawer-cart-name">${featuredBadge}<span class="cart-drawer-cart-name-text">${escapeHtml(cart.name)}</span></span>`;

    return `
      <div class="cart-drawer-cart${isSelected ? ' selected' : ''}" data-cart-id="${cart.id}">
        <div class="cart-drawer-cart-row">
          <button type="button" class="cart-drawer-chevron-btn" title="${isExpanded ? 'Collapse' : 'Expand'}">${chevron}</button>
          ${nameHtml}
          <span class="cart-drawer-cart-count">${cart.item_count}</span>
          ${!cart.is_default && !isEditing ? `
            <button type="button" class="cart-drawer-rename-btn" title="Rename this cart">✏️</button>
            <button type="button" class="cart-drawer-delete-btn" title="Delete this cart">🗑</button>
          ` : ''}
        </div>
        ${isSelected ? `<div class="cart-drawer-selected-tag">Agent Picks add here</div>` : ''}
        ${cart.is_default ? `<div class="cart-drawer-hint cart-drawer-default-hint">Shows on your landing page and the map's Featured badge.</div>` : ''}
        ${isExpanded ? `<div class="cart-drawer-cart-items">${this.buildCartItemsHtml(cart.id, detail, isLoading)}</div>` : ''}
      </div>
    `;
  }

  private buildCartItemsHtml(cartId: string, detail: AgentCartDetail | undefined, isLoading: boolean): string {
    if (isLoading || !detail) return `<p class="cart-drawer-hint">Loading...</p>`;
    const items = detail.items;
    if (items.length === 0) {
      return `<p class="cart-drawer-hint">Nothing in this cart yet - click a listing or neighborhood on the map.</p>`;
    }

    return items.map((item, i) => `
      <div class="cart-drawer-item" data-cart-id="${cartId}" data-item-id="${item.id}">
        <div class="cart-drawer-item-row">
          <span class="cart-drawer-item-type-icon">${item.item_type === 'neighborhood' ? '📍' : '🏠'}</span>
          <span class="cart-drawer-item-label">${escapeHtml(this.itemLabels.get(item.item_id) || '...')}</span>
        </div>
        <div class="cart-drawer-item-actions">
          <button type="button" class="cart-drawer-item-move-up" ${i === 0 ? 'disabled' : ''} title="Move up">↑</button>
          <button type="button" class="cart-drawer-item-move-down" ${i === items.length - 1 ? 'disabled' : ''} title="Move down">↓</button>
          <button type="button" class="cart-drawer-item-remove" title="Remove">Remove</button>
        </div>
      </div>
    `).join('');
  }

  private attachBodyListeners(): void {
    this.body.querySelector('.cart-drawer-new-btn')?.addEventListener('click', () => {
      void this.createNewCart().then(() => this.renderBody());
    });

    this.body.querySelectorAll('.cart-drawer-cart').forEach((rowEl) => {
      const cartId = (rowEl as HTMLElement).dataset.cartId;
      if (!cartId) return;

      // Selecting (click anywhere on the row except the chevron/rename/
      // delete buttons) picks this cart for Agent Pick AND expands it.
      rowEl.querySelector('.cart-drawer-cart-name')?.addEventListener('click', () => {
        void this.selectCart(cartId).then(() => this.renderBody());
      });
      rowEl.querySelector('.cart-drawer-cart-count')?.addEventListener('click', () => {
        void this.selectCart(cartId).then(() => this.renderBody());
      });

      // The chevron only toggles expansion, independent of selection - an
      // agent can peek into another cart without changing where new picks
      // land.
      rowEl.querySelector('.cart-drawer-chevron-btn')?.addEventListener('click', () => {
        void this.toggleExpanded(cartId).then(() => this.renderBody());
      });

      rowEl.querySelector('.cart-drawer-rename-btn')?.addEventListener('click', () => {
        this.editingCartId = cartId;
        this.renderBody();
      });

      // Inline name input, shown instead of the rename button while
      // editingCartId === this cart - commits on Enter/blur, cancels on
      // Escape without saving. Focused+selected immediately so typing
      // replaces the existing name right away (matters most for a
      // brand-new "Untitled cart").
      const nameInput = rowEl.querySelector<HTMLInputElement>('.cart-drawer-cart-name-input');
      if (nameInput) {
        nameInput.focus();
        nameInput.select();
        let committed = false;
        const commit = () => {
          if (committed) return;
          committed = true;
          const cart = this.carts.find((c) => c.id === cartId);
          const name = nameInput.value.trim();
          this.editingCartId = null;
          if (cart && name && name !== cart.name) {
            void this.renameCart(cartId, name).then(() => this.renderBody());
          } else {
            this.renderBody();
          }
        };
        nameInput.addEventListener('blur', commit);
        nameInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            nameInput.blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            committed = true;
            this.editingCartId = null;
            this.renderBody();
          }
        });
      }

      rowEl.querySelector('.cart-drawer-delete-btn')?.addEventListener('click', () => {
        if (!confirm('Delete this cart? This cannot be undone.')) return;
        void this.deleteCart(cartId).then(() => this.renderBody());
      });

      rowEl.querySelectorAll('.cart-drawer-item').forEach((itemRow) => {
        const itemId = (itemRow as HTMLElement).dataset.itemId;
        if (!itemId) return;

        itemRow.querySelector('.cart-drawer-item-move-up')?.addEventListener('click', (e) => {
          e.stopPropagation();
          void this.reorderItem(cartId, itemId, 'up').then(() => this.renderBody());
        });
        itemRow.querySelector('.cart-drawer-item-move-down')?.addEventListener('click', (e) => {
          e.stopPropagation();
          void this.reorderItem(cartId, itemId, 'down').then(() => this.renderBody());
        });
        itemRow.querySelector('.cart-drawer-item-remove')?.addEventListener('click', (e) => {
          e.stopPropagation();
          void this.removeItem(cartId, itemId).then(() => this.renderBody());
        });
      });
    });
  }

  // -------- Cart data operations --------

  private async loadCarts(): Promise<void> {
    try {
      this.carts = await this.client.getAgentCarts();
      this.cartsLoaded = true;
      // Default to the Featured Listings cart on first load (every agent
      // always has one); otherwise keep whatever's already selected, or
      // fall back to the first cart if the previously-selected one is
      // somehow gone.
      if (!this.selectedCartId || !this.carts.some((c) => c.id === this.selectedCartId)) {
        const fallback = this.carts.find((c) => c.is_default) ?? this.carts[0];
        if (fallback) await this.selectCart(fallback.id, { expand: false });
      }
    } catch (error) {
      console.error('[CartDrawer] Failed to load carts:', error);
      this.cartsLoaded = true;
    }
  }

  private async createNewCart(): Promise<void> {
    try {
      const { id } = await this.client.createAgentCart();
      await this.loadCarts();
      await this.selectCart(id);
      // Starts straight in the inline-rename state (per Todd 2026-11-01) -
      // the caller's renderBody() right after this resolves renders the
      // input already focused, so the agent can just type over "Untitled
      // cart" immediately instead of a separate rename step.
      this.editingCartId = id;
    } catch (error) {
      console.error('[CartDrawer] Failed to create cart:', error);
      this.showToast('Failed to create a new cart');
    }
  }

  /** Marks a cart as the Agent Pick target. Expands it too (so you can see
   *  what you just added) unless explicitly suppressed - used by
   *  loadCarts' initial-selection fallback, which shouldn't force every
   *  cart open on first render. */
  private async selectCart(id: string, opts: { expand?: boolean } = {}): Promise<void> {
    this.selectedCartId = id;
    if (opts.expand !== false && !this.expandedCartIds.has(id)) {
      await this.expandCart(id);
    } else if (!this.cartDetails.has(id)) {
      await this.fetchCartDetail(id);
    }
  }

  private async toggleExpanded(id: string): Promise<void> {
    if (this.expandedCartIds.has(id)) {
      this.expandedCartIds.delete(id);
    } else {
      await this.expandCart(id);
    }
  }

  private async expandCart(id: string): Promise<void> {
    this.expandedCartIds.add(id);
    if (!this.cartDetails.has(id)) {
      await this.fetchCartDetail(id);
    }
  }

  private async fetchCartDetail(id: string): Promise<void> {
    this.loadingCartIds.add(id);
    try {
      const detail = await this.client.getAgentCart(id);
      this.cartDetails.set(id, detail);
      await this.resolveLabels(detail);
    } catch (error) {
      console.error('[CartDrawer] Failed to load cart:', error);
      this.cartDetails.delete(id);
    } finally {
      this.loadingCartIds.delete(id);
    }
  }

  private async renameCart(id: string, name: string): Promise<void> {
    try {
      await this.client.updateAgentCart(id, { name });
      await this.loadCarts();
    } catch (error) {
      console.error('[CartDrawer] Failed to rename cart:', error);
      this.showToast('Failed to rename cart');
    }
  }

  private async deleteCart(id: string): Promise<void> {
    try {
      await this.client.deleteAgentCart(id);
      this.cartDetails.delete(id);
      this.expandedCartIds.delete(id);
      if (this.selectedCartId === id) this.selectedCartId = null;
      await this.loadCarts();
    } catch (error) {
      console.error('[CartDrawer] Failed to delete cart:', error);
      this.showToast('Failed to delete cart - it may be the undeletable Featured Listings cart');
    }
  }

  private async refreshCart(id: string): Promise<void> {
    // Notify listeners (e.g. AgentMap's Featured badge) that a cart's
    // items changed, whether or not it's currently expanded in this UI -
    // the mutation already happened via addItem/removeItem/reorderItem.
    window.dispatchEvent(new CustomEvent('cart-items-changed', { detail: { cartId: id } }));

    if (!this.expandedCartIds.has(id)) {
      this.cartDetails.delete(id);
      return;
    }
    await this.fetchCartDetail(id);
    this.renderBody();
  }

  private async removeItem(cartId: string, itemId: string): Promise<void> {
    try {
      await this.client.removeCartItem(cartId, itemId);
      await this.refreshCart(cartId);
      await this.loadCarts();
    } catch (error) {
      console.error('[CartDrawer] Failed to remove item:', error);
      this.showToast('Failed to remove');
    }
  }

  private async reorderItem(cartId: string, itemId: string, direction: 'up' | 'down'): Promise<void> {
    const detail = this.cartDetails.get(cartId);
    if (!detail) return;
    const items = detail.items;
    const index = items.findIndex((i) => i.id === itemId);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (index === -1 || swapIndex < 0 || swapIndex >= items.length) return;

    const a = items[index]!;
    const b = items[swapIndex]!;
    try {
      await Promise.all([
        this.client.updateCartItem(cartId, a.id, { sortOrder: b.sort_order }),
        this.client.updateCartItem(cartId, b.id, { sortOrder: a.sort_order }),
      ]);
      await this.refreshCart(cartId);
    } catch (error) {
      console.error('[CartDrawer] Failed to reorder item:', error);
      this.showToast('Failed to reorder');
    }
  }

  private async resolveLabels(detail: AgentCartDetail): Promise<void> {
    const listingIds = detail.items.filter((i) => i.item_type === 'listing').map((i) => i.item_id);
    const neighborhoodItems = detail.items.filter((i) => i.item_type === 'neighborhood');
    const tmkIds = detail.items.filter((i) => i.item_type === 'tmk').map((i) => i.item_id);

    for (const item of neighborhoodItems) {
      const label = this.resolveNeighborhoodLabel(item.item_id);
      if (label) this.itemLabels.set(item.item_id, label);
    }

    if (listingIds.length > 0) {
      try {
        const resolved = await this.resolveListingLabels(listingIds);
        resolved.forEach(({ label }, id) => this.itemLabels.set(id, label));
      } catch (error) {
        console.error('[CartDrawer] Failed to resolve listing labels:', error);
      }
    }

    if (tmkIds.length > 0) {
      try {
        const resolved = await this.resolveTmkLabels(tmkIds);
        resolved.forEach(({ label }, id) => this.itemLabels.set(id, label));
      } catch (error) {
        console.error('[CartDrawer] Failed to resolve TMK labels:', error);
      }
    }
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// escapeHtml's textContent->innerHTML roundtrip escapes &/</>  but not
// quotes (nothing requires that in a text node) - unsafe to reuse as-is
// for an attribute value like the inline rename input's value="...".
function escapeAttr(text: string): string {
  return escapeHtml(text).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Exported so ListDrawer.ts (a deliberately separate component, not a
// shared base class - see its own file comment) can reuse this exact
// stylesheet for the chrome that's visually identical between the two
// (handle, panel, resize grip, list-header, "+ New" button, row/chevron/
// rename/delete buttons, toast) - idempotent, so it's safe to call from
// both regardless of instantiation order.
export function injectCartDrawerStyles(): void {
  if (document.getElementById('cart-drawer-styles')) return;
  const style = document.createElement('style');
  style.id = 'cart-drawer-styles';
  style.textContent = `
.cart-drawer-wrapper {
  position: fixed;
  top: calc(var(--nav-height, 48px) + 16px);
  left: 0;
  bottom: 2.5vh;
  z-index: 735;
  pointer-events: none;
  font-family: inherit;
}

.cart-drawer-wrapper.cart-drawer-unavailable {
  display: none;
}

.cart-drawer-wrapper input,
.cart-drawer-wrapper select,
.cart-drawer-wrapper button {
  font-family: inherit;
}

.cart-drawer-handle {
  position: fixed;
  left: 0;
  top: calc(50vh + var(--nav-height, 48px) / 2);
  transform: translateY(-50%);
  width: 40px;
  background: rgba(255, 255, 255, 0.98);
  padding: 10px 6px;
  border-radius: 0 8px 8px 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  box-shadow:
    3px 0 8px rgba(37, 99, 235, 0.2),
    6px 0 20px rgba(37, 99, 235, 0.1);
  transition: padding-right 0.2s ease, box-shadow 0.2s ease, border-right-color 0.2s ease, left 0.3s ease;
  border-right: 2px solid rgba(37, 99, 235, 0.45);
  pointer-events: auto;
  z-index: 10;
}

/* Kill the transition while actively dragging, so the handle tracks the
   cursor instead of easing (same reasoning as UnifiedDrawer's .resizing). */
.cart-drawer-wrapper.resizing .cart-drawer-panel,
.cart-drawer-wrapper.resizing .cart-drawer-handle {
  transition: none !important;
}

.cart-drawer-handle:hover {
  padding-right: 10px;
  box-shadow:
    4px 0 12px rgba(37, 99, 235, 0.3),
    8px 0 25px rgba(37, 99, 235, 0.15);
  border-right-color: rgba(37, 99, 235, 0.65);
}

.cart-drawer-handle-icon {
  font-size: 14px;
  line-height: 1;
}

.cart-drawer-handle-arrow {
  flex-shrink: 0;
  transform: rotate(270deg);
  transition: transform 0.2s ease;
}

.cart-drawer-wrapper.open .cart-drawer-handle-arrow {
  transform: rotate(90deg);
}

.cart-drawer-handle-label {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-text, #1f2937);
  letter-spacing: 0.5px;
  user-select: none;
}

.cart-drawer-panel {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  background: var(--color-bg, #fff);
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 12px 0;
  display: flex;
  flex-direction: column;
  width: 0;
  overflow: hidden;
  opacity: 0;
  transition: width 0.3s ease, opacity 0.2s ease;
}

.cart-drawer-wrapper.open .cart-drawer-panel {
  opacity: 1;
  pointer-events: auto;
}

/* Resize grip on the right edge of the panel (opposite side from
   UnifiedDrawer's, which is anchored right and grips on its left edge). */
.cart-drawer-resize {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 8px;
  cursor: ew-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.cart-drawer-resize-grip {
  width: 4px;
  height: 48px;
  background: #2563eb;
  border-radius: 2px;
  opacity: 0.55;
  box-shadow: 0 0 6px rgba(37, 99, 235, 0.35);
  transition: opacity 0.2s ease, background 0.2s ease, height 0.2s ease;
  position: relative;
}

.cart-drawer-resize-grip::before,
.cart-drawer-resize-grip::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 1px;
}
.cart-drawer-resize-grip::before { top: 30%; }
.cart-drawer-resize-grip::after { bottom: 30%; }

.cart-drawer-resize:hover .cart-drawer-resize-grip {
  opacity: 1;
  background: #1d4ed8;
  height: 64px;
  box-shadow: 0 0 12px rgba(37, 99, 235, 0.7);
}

.cart-drawer-resize.dragging .cart-drawer-resize-grip {
  opacity: 1;
  background: #1d4ed8;
}

@media (max-width: 767px) {
  .cart-drawer-resize {
    display: none; /* touch hit area too small to be useful */
  }
}

.cart-drawer-body {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 1rem;
  padding-right: 1.25rem; /* reserve space for the resize grip */
  flex: 1;
  min-height: 0;
  overflow: hidden; /* .cart-drawer-list below does the actual scrolling, so the "+ New" header stays pinned */
}

.cart-drawer-hint {
  font-size: 0.85rem;
  color: var(--color-text-light, #6b7280);
  margin: 0;
  line-height: 1.4;
}

.cart-drawer-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-text, #1f2937);
  flex-shrink: 0;
}

.cart-drawer-new-btn {
  padding: 0.3rem 0.6rem;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 6px;
  background: var(--color-bg, #fff);
  color: var(--color-text, #1f2937);
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
}

.cart-drawer-new-btn:hover {
  background: var(--color-bg-light, #f9fafb);
}

/* Carts all visible as a scrollable list (per Todd 2026-10-25), replacing
   the old single dropdown + one expanded cart below it. */
.cart-drawer-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.cart-drawer-cart {
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 8px;
  padding: 0.5rem 0.6rem;
  background: var(--color-bg, #fff);
}

.cart-drawer-cart.selected {
  border-color: var(--color-primary, #2563eb);
  background: var(--color-primary-light, #dbeafe);
}

.cart-drawer-cart-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.cart-drawer-chevron-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-light, #6b7280);
  cursor: pointer;
}

.cart-drawer-chevron {
  width: 14px;
  height: 14px;
  transition: transform 0.15s ease;
}

.cart-drawer-chevron.expanded {
  transform: rotate(90deg);
}

.cart-drawer-cart-name {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--color-text, #1f2937);
  cursor: pointer;
}

.cart-drawer-cart-name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Inline rename input (per Todd 2026-11-01) - same flex slot/font as the
   static name it replaces, just with a visible edit affordance. */
.cart-drawer-cart-name-input {
  flex: 1;
  min-width: 0;
  font-size: 0.85rem;
  font-weight: 500;
  font-family: inherit;
  color: var(--color-text, #1f2937);
  background: var(--color-bg, #fff);
  border: 1px solid var(--color-primary, #2563eb);
  border-radius: 4px;
  padding: 1px 4px;
}

.cart-drawer-cart-name-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--color-primary-light, #dbeafe);
}

.cart-drawer-featured-badge {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
}

.cart-drawer-cart-count {
  flex-shrink: 0;
  font-size: 0.75rem;
  color: var(--color-text-light, #6b7280);
  cursor: pointer;
}

.cart-drawer-rename-btn,
.cart-drawer-delete-btn {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 6px;
  background: var(--color-bg, #fff);
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0;
}

.cart-drawer-delete-btn:hover {
  background: var(--color-danger-light, #fee2e2);
  border-color: var(--color-danger, #ef4444);
}

.cart-drawer-rename-btn:hover {
  background: var(--color-bg-light, #f9fafb);
}

.cart-drawer-selected-tag {
  margin-top: 0.3rem;
  margin-left: 1.65rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--color-primary, #2563eb);
}

.cart-drawer-default-hint {
  margin-top: 0.3rem;
  margin-left: 1.65rem;
  font-size: 0.75rem;
}

.cart-drawer-cart-items {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--color-border, #e5e7eb);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cart-drawer-item {
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 8px;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  background: var(--color-bg-light, #f9fafb);
}

.cart-drawer-item-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}

.cart-drawer-item-type-icon {
  flex-shrink: 0;
  font-size: 0.85rem;
}

.cart-drawer-item-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--color-text, #1f2937);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cart-drawer-item-actions {
  display: flex;
  gap: 0.3rem;
  justify-content: flex-end;
}

.cart-drawer-item-actions button {
  padding: 0.2rem 0.45rem;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 5px;
  background: var(--color-bg, #fff);
  color: var(--color-text-light, #6b7280);
  font-size: 0.72rem;
  cursor: pointer;
}

.cart-drawer-item-actions button:hover:not(:disabled) {
  background: var(--color-bg-light, #f9fafb);
  color: var(--color-text, #1f2937);
}

.cart-drawer-item-actions button:disabled {
  opacity: 0.35;
  cursor: default;
}

.cart-drawer-item-remove:hover:not(:disabled) {
  background: var(--color-danger-light, #fee2e2) !important;
  border-color: var(--color-danger, #ef4444) !important;
  color: var(--color-danger, #ef4444) !important;
}

/* Toast - appended to document.body (not the drawer panel) so it's
   visible even if the panel is closed when a map click adds an item. */
.cart-drawer-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(8px);
  background: #1f2937;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-family: inherit;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 950;
  opacity: 0;
  transition: opacity 0.2s ease, transform 0.2s ease;
  pointer-events: none;
}

.cart-drawer-toast.show {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

@media (max-width: 767px) {
  .cart-drawer-body {
    width: min(${DEFAULT_WIDTH}px, 85vw);
  }
}
  `;
  document.head.appendChild(style);
}
